import {
  Box,
  Card,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useAppSelector } from 'store/hook';
import { exportPlanDataToExcel } from 'views/FleetPlanner/utils/exportToExcel';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import {
  AddCircle,
  ArrowCircleLeftOutlined,
  Circle,
  Download,
  InfoOutlined,
  ListAlt,
  Visibility,
} from '@mui/icons-material';
import { PlanHistoryModal } from '../modals/PlanHistoryModal';
import { useNavigate } from 'react-router-dom';
import type dayjs from 'dayjs';
import { type Dayjs } from 'dayjs';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { mergeDispatchPlans } from '../utils/dataFormatters';
import { useTranslation } from 'react-i18next';

const getPlanColor = (planType: string) => {
  switch (planType) {
    case 'single_active':
      return '#689F38'; // Blue
    case 'multi_active':
      return '#3B82F6'; // Blue
    case 'preview':
      return '#d97706'; // Orange
    case 'no_plan':
      return '#F44336'; // Red
    default:
      return '#9CA3AF'; // Grey
  }
};

export const ActivePlanCard = ({
  selectedDateRange,
  date,
  handleDateChange,
  currentShiftLabel,
}: {
  selectedDateRange: string;
  date: dayjs.Dayjs | null;
  handleDateChange: (
    range: string,
    date: Dayjs | null,
    shouldFetchActivePlan: boolean,
    isButtonClick?: boolean,
    options?: {
      shiftViewModeOverride?: 'current' | 'shiftWise';
      shiftNumberOverride?: number;
    }
  ) => Promise<void>;
  currentShiftLabel?: string;
}) => {
  const { t } = useTranslation();
  const { customerTz } = useCustomerInfo();

  const approvedPlanData = useAppSelector(
    (state) => state.fleetPlanningReducer.dispatchPlanSummary
  );

  const mergedPlans = mergeDispatchPlans(approvedPlanData.plans);
  const planType = useAppSelector(
    (state) => state.fleetPlanningReducer.dispatchPlanSummary.planType
  );

  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);

  const handleExportClick = () => {
    const planName = approvedPlanData?.planName
      ? `${approvedPlanData?.planName}.xlsx`
      : 'approved_schedule.xlsx';

    exportPlanDataToExcel(
      mergedPlans.planData,
      planName,
      'configured',
      customerTz
    );
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleCreateNewPlan = () => {
    navigate('/fleet-planner/create-dispatch-plan');
  };

  const handleExitPreviewMode = () => {
    void handleDateChange(selectedDateRange, date, true, true);
  };
  // buttons
  const ChangePlanButton = () => {
    return (
      <CustomLoadingButton
        variant="contained"
        color="secondary"
        sx={{
          px: 2,
          borderRadius: 2,
          height: '36px',
        }}
        onClick={handleOpenModal}
        startIcon={<ListAlt />}
      >
        {planType === 'no_plan' && selectedDateRange === 'Today'
          ? 'Apply Plan'
          : 'Change Plan'}
      </CustomLoadingButton>
    );
  };

  const CreateNewPlanButton = () => {
    const { t } = useTranslation();
    return (
      <CustomLoadingButton
        variant="contained"
        color="primary"
        sx={{
          px: 2,
          borderRadius: 2,
          height: '36px',
        }}
        startIcon={<AddCircle />}
        onClick={handleCreateNewPlan}
      >
        {t('create_new')}
      </CustomLoadingButton>
    );
  };

  const ExportPlanButton = () => {
    return (
      <CustomLoadingButton
        variant="contained"
        color="primary"
        sx={{
          px: 2,
          borderRadius: 2,
          height: '36px',
        }}
        startIcon={<Download />}
        onClick={handleExportClick}
      >
        Export
      </CustomLoadingButton>
    );
  };

  const ExitPreviewModeButton = () => {
    const { t } = useTranslation();
    return (
      <Box display={'flex'} alignItems={'center'}>
        <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
          <IconButton size="small" onClick={handleExitPreviewMode}>
            <Tooltip title={t('exit_preview_mode')} arrow>
              <ArrowCircleLeftOutlined />
            </Tooltip>
          </IconButton>
        </Box>
        <Box>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: '70px',
            }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Card
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: getPlanColor(planType),
            transition: 'border-color 0.5s ease',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: 'none',
          }}
        >
          <Box
            sx={{
              height: '6px',
              width: '100%',
              bgcolor: getPlanColor(planType),
              transition: 'background-color 0.5s ease',
            }}
          />

          <Box
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,

              borderRadius: 2,
            }}
          >
            {planType === 'no_plan' ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{
                  width: '100%',
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  p={2.5}
                  borderRadius={2}
                  sx={{
                    backgroundColor: '#F44336',
                    color: '#FFFFFF',
                  }}
                >
                  <InfoOutlined sx={{ fontSize: 26 }} />
                  <Box>
                    <Typography variant="body1" fontWeight={700}>
                      {selectedDateRange === 'Today'
                        ? 'No active plan set for today.'
                        : 'No active plan was set for the day.'}
                    </Typography>
                    {selectedDateRange === 'Today' && (
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {t(
                          'you_have_approved_plans_available_set_one_active_for_today_o'
                        )}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box display="flex" gap={1}>
                  <ChangePlanButton />
                  {selectedDateRange === 'Today' && <CreateNewPlanButton />}
                </Box>
              </Box>
            ) : planType === 'multi_active' ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{
                  width: '100%',
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  p={2.5}
                  borderRadius={2}
                  sx={{
                    backgroundColor: getPlanColor(planType),
                    color: '#FFFFFF',
                  }}
                >
                  <InfoOutlined sx={{ fontSize: 26 }} />
                  <Box>
                    <Typography variant="body1" fontWeight={700}>
                      {t('multiple_active_plans_detected')}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {t('more_than_one_plan_was_active_for_the_day')}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" gap={1}>
                  <ChangePlanButton />
                </Box>
              </Box>
            ) : (
              <>
                {planType === 'preview' && <ExitPreviewModeButton />}
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Chip
                      icon={
                        planType === 'single_active' ? (
                          <Circle
                            sx={{
                              width: 8,
                              height: 8,
                              color: '#FFFFFF !important',
                            }}
                          />
                        ) : (
                          <Visibility
                            sx={{
                              width: 14,
                              height: 14,
                              color: `#FFFFFF !important`,
                            }}
                          />
                        )
                      }
                      label={
                        planType === 'single_active'
                          ? 'ACTIVE PLAN'
                          : 'PREVIEW MODE'
                      }
                      sx={{
                        height: 24,
                        fontSize: '0.70rem',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        borderRadius: '12px',
                        bgcolor: getPlanColor(planType),
                        color: '#FFFFFF',
                        border: '1px solid',
                        borderColor:
                          planType === 'preview'
                            ? 'rgba(245, 158, 11, 0.2)'
                            : 'rgba(34, 197, 94, 0.2)',
                        '& .MuiChip-icon': { ml: 1 },
                      }}
                    />
                  </Box>

                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontWeight: 700, letterSpacing: '-0.025em' }}
                      >
                        {approvedPlanData?.planName ?? ''}
                      </Typography>
                      {currentShiftLabel && (
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          {`Showing metrics for ${currentShiftLabel}`}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <ChangePlanButton />
                  <ExportPlanButton />
                </Box>
              </>
            )}
          </Box>
        </Card>
      </Box>

      {openModal && (
        <PlanHistoryModal
          open={openModal}
          onClose={handleCloseModal}
          date={date}
          handleDateChange={handleDateChange}
          selectedDateRange={selectedDateRange}
        />
      )}
    </>
  );
};
